import React from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { UserRole } from '@features/authentication/types';
import { useInsightSummary } from '@features/insight/hooks/useInsight';
import { useDashboardInsightFilters } from '../hooks/useDashboardInsightFilters';
import { QuickStatsContent } from './QuickStatsContent';
import { QuickStatsCTACard } from './QuickStatsCTACard';

/**
 * Dashboard Quick Stats widget orchestrator.
 *
 * - SYSTEM_ADMIN: renders a CTA card linking to statistics
 * - COURSE_REPRESENTATIVE: renders nothing (no analytics access)
 * - Other roles: fetches summary data and renders QuickStatsContent
 */
export const QuickStatsWidget: React.FC = () => {
  const { t } = useTranslation();
  const { filters, queryEnabled, currentRole } = useDashboardInsightFilters();
  const { data: summary, isLoading, isError, refetch } = useInsightSummary(filters, queryEnabled);

  if (currentRole === UserRole.COURSE_REPRESENTATIVE || currentRole === null) {
    return null;
  }

  if (currentRole === UserRole.SYSTEM_ADMIN) {
    return (
      <Box sx={{ mb: 4 }}>
        <QuickStatsCTACard />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ mb: 4 }}>
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
            >
              {t('insight.retry')}
            </Button>
          }
        >
          <Typography variant="body2">{t('dashboard.quickStats.error')}</Typography>
        </Alert>
      </Box>
    );
  }

  if (!queryEnabled) {
    return null;
  }

  if (!summary && !isLoading) {
    return null;
  }

  const emptySummary = {
    domainCount: 0,
    subjectCount: 0,
    surveyCount: 0,
    instanceCount: 0,
    responseCount: 0,
    completedResponseCount: 0,
    completionRate: 0,
    avgResponsesPerSurvey: 0,
  };

  return (
    <Box sx={{ mb: 4 }}>
      <QuickStatsContent
        summary={summary ?? emptySummary}
        currentRole={currentRole}
        isLoading={isLoading}
      />
    </Box>
  );
};
